import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", data: null, attempts: 0 });

  const poll = useCallback(async (attempts) => {
    if (!sessionId) {
      setState({ status: "error", data: null, attempts });
      return;
    }
    if (attempts >= 10) {
      setState({ status: "timeout", data: null, attempts });
      return;
    }
    try {
      const { data } = await api.get(`/subscriptions/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setState({ status: "paid", data, attempts });
        return;
      }
      if (data.status === "expired") {
        setState({ status: "expired", data, attempts });
        return;
      }
      setTimeout(() => poll(attempts + 1), 2000);
    } catch {
      setTimeout(() => poll(attempts + 1), 2000);
    }
  }, [sessionId]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div className="min-h-screen grid place-items-center bg-[#FAFAF9] p-6" data-testid="billing-success-page">
      <div className="max-w-md w-full bg-white border border-stone-200 rounded-3xl p-10 text-center shadow-sm">
        {state.status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-amber-500 mx-auto animate-spin" />
            <h1 className="mt-6 font-display text-2xl font-semibold">Confirming payment…</h1>
            <p className="mt-2 text-stone-500 text-sm">Hang tight — this usually takes a moment.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
            <h1 className="mt-6 font-display text-2xl font-semibold">You're subscribed!</h1>
            <p className="mt-2 text-stone-500">
              {state.data?.tier?.toUpperCase()} tier is now active. Enjoy HomeSchool Hub.
            </p>
            <Link to="/dashboard" data-testid="success-back-dashboard">
              <Button className="mt-6 rounded-full bg-amber-500 hover:bg-amber-600 text-white">Back to dashboard</Button>
            </Link>
          </>
        )}
        {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto" />
            <h1 className="mt-6 font-display text-2xl font-semibold">Payment not completed</h1>
            <p className="mt-2 text-stone-500">Your session expired or we couldn't confirm the payment.</p>
            <Link to="/dashboard/billing">
              <Button className="mt-6 rounded-full bg-stone-900 hover:bg-stone-800 text-white">Try again</Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
